import { FC, Fragment } from 'react';
import { useLocale } from '@app/core/hooks/useLocale';
import { amountRules } from '@app/shared/components/cell-value/CellValueConfigurations';
import {
  OpmSampleWaterfallsDto,
  OpmWaterfallConditionDto,
} from '@app/shared/models/contracts/opm-calculation-results-dto';
import { Operator } from '@app/shared/models/contracts/enums/shared-enums';
import OrdinaryEquityRow from './OrdinaryEquityRow';
import { numberValueFormatter } from '@app/shared/formatters/number-value-formatter';
import {
  getTrancheForColumn,
  NOT_APPLICABLE,
  WaterfallCellValue,
} from './sample-waterfall-column-helpers';

type BaseValueType = WaterfallCellValue;

interface ToeOeConditionsRowsProps {
  oeConditions: OpmWaterfallConditionDto[];
  hasBuybacks: boolean;
  hasDividends: boolean;
  columnKeys: string[];
  hasMultipleTranches: boolean;
  instrumentId: string;
  trancheKey: string;
  waterfallResults: Dictionary<OpmSampleWaterfallsDto>;
}

const ToeOeConditionsRows: FC<ToeOeConditionsRowsProps> = ({
  oeConditions,
  hasBuybacks,
  hasDividends,
  columnKeys,
  hasMultipleTranches,
  instrumentId,
  trancheKey,
  waterfallResults,
}) => {
  const { l } = useLocale();
  const interpolationOption = { escapeValue: false };
  const hasDeductions = hasBuybacks || hasDividends;

  const getOeCondition = (columnKey: string, conditionIndex: number) =>
    getTrancheForColumn(waterfallResults, columnKey, instrumentId, trancheKey)
      ?.ordinaryEquityMetrics?.conditions[conditionIndex];

  const getOeConditionEquivalentHurdle = (
    conditionIndex: number
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const condition = getOeCondition(colKey, conditionIndex);
      return condition ? condition.toeHurdleDetails?.initialHurdle ?? null : NOT_APPLICABLE;
    };
  };

  const getOeConditionToeHurdleValue = (
    conditionIndex: number,
    valueKey: 'cumulativeRedemptions' | 'cumulativeDividends'
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const condition = getOeCondition(colKey, conditionIndex);
      return condition ? condition.toeHurdleDetails?.[valueKey] ?? null : NOT_APPLICABLE;
    };
  };

  const getOeConditionNetHurdle = (
    conditionIndex: number
  ): ((columnKey: string) => BaseValueType) => {
    return (colKey: string) => {
      const condition = getOeCondition(colKey, conditionIndex);
      return condition ? condition.toeHurdleDetails?.finalHurdle ?? null : NOT_APPLICABLE;
    };
  };

  return (
    <>
      {oeConditions.map((oeCondition: OpmWaterfallConditionDto, condIndex: number) => {
        const formattedValue = numberValueFormatter({
          value: Number(oeCondition.testValue),
          ...amountRules,
        });
        const operatorSymbol = Operator[oeCondition.operator as keyof typeof Operator];
        const grossLabel = l('_ToeConditionPlaceholders', {
          operator: operatorSymbol,
          value: formattedValue,
          interpolation: interpolationOption,
        });
        const netLabel = l('_NetOeHurdleConditionPlaceholders', {
          operator: operatorSymbol,
          value: formattedValue,
          interpolation: interpolationOption,
        });

        return (
          <Fragment key={`toe-condition-${condIndex}`}>
            <OrdinaryEquityRow
              columnKeys={columnKeys}
              label={grossLabel}
              valueSelector={getOeConditionEquivalentHurdle(condIndex)}
              rowTestId={`toe-condition-${condIndex + 1}-title`}
              trancheIndentation={hasMultipleTranches}
            />
            {hasBuybacks && (
              <OrdinaryEquityRow
                columnKeys={columnKeys}
                label={l('_LessCumulativeBuybacks')}
                valueSelector={getOeConditionToeHurdleValue(condIndex, 'cumulativeRedemptions')}
                rowTestId={`toe-less-buybacks-${condIndex + 1}-title`}
                trancheIndentation={hasMultipleTranches}
                hideRowIfEmpty
              />
            )}
            {hasDividends && (
              <OrdinaryEquityRow
                columnKeys={columnKeys}
                label={l('_LessCumulativeDividends')}
                valueSelector={getOeConditionToeHurdleValue(condIndex, 'cumulativeDividends')}
                rowTestId={`toe-less-dividends-${condIndex + 1}-title`}
                trancheIndentation={hasMultipleTranches}
                hideRowIfEmpty
              />
            )}
            {hasDeductions && (
              <OrdinaryEquityRow
                columnKeys={columnKeys}
                label={netLabel}
                valueSelector={getOeConditionNetHurdle(condIndex)}
                rowTestId={`toe-net-condition-${condIndex + 1}-title`}
                trancheIndentation={hasMultipleTranches}
                hideRowIfEmpty
                bold
              />
            )}
          </Fragment>
        );
      })}
    </>
  );
};

export default ToeOeConditionsRows;
